import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { isMoment } from 'moment';

import { map, Observable, take, timeout } from 'rxjs';

import { isArray, isObject } from 'lodash-es';

import { Helper } from '@core/util';
import { GenericApiOption } from '../type';
import { GENERIC_API_SERVICE, GENERIC_API_SOURCE } from '@core/data-access';
import { AppConfig, AppConfigService } from '@core/app-config/data-access';

@Injectable({ providedIn: 'root' })
export class GenericApiService {
  private readonly configService = inject(AppConfigService);
  private readonly httpClient = inject(HttpClient);
  private readonly requestTimeout = 60000; // 60 seconds
  private readonly defaultOptions: GenericApiOption = {
    loader: true,
    pretreatmentResponse: true,
    excludeFields: [],
    requestOptions: { headers: this.createHeaders() },
  };

  static pretreatmentDataRequest(data: Record<string, any>, options?: GenericApiOption) {
    data = GenericApiService.formatMomentObject(data);
    data = Helper.removeEmpty(data, options?.excludeFields);
    return data;
  }

  static formatMomentObject(data: any): any {
    if (isMoment(data)) {
      return data.format();
    }

    if (isArray(data)) {
      return data.map(item => GenericApiService.formatMomentObject(item));
    }

    if (isObject(data)) {
      const res: { [x in string]: any } = { ...data };
      const keys = Object.keys(res);
      keys.forEach(key => (res[key] = GenericApiService.formatMomentObject(res[key])));
      return res;
    }

    return data;
  }

  static normalizeOption(options: GenericApiOption, optionsMatching?: GenericApiOption): GenericApiOption {
    if (!optionsMatching) {
      return options;
    }

    let results: GenericApiOption = { ...options };
    optionsMatching.requestOptions = { ...results.requestOptions, ...optionsMatching.requestOptions };
    results = { ...results, ...optionsMatching };

    if (optionsMatching.exposeHeaders) {
      Object.keys(optionsMatching.exposeHeaders).forEach(key => {
        const headers = results.requestOptions?.headers as HttpHeaders;
        if (optionsMatching.exposeHeaders && results.requestOptions) {
          results.requestOptions.headers = headers.set(key, optionsMatching.exposeHeaders[key]);
        }
      });
    }

    return results;
  }

  createHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json', [GENERIC_API_SOURCE]: GENERIC_API_SERVICE });
  }

  createAPIURL(args: { url: string; params: Record<string, string>; config: AppConfig }): string {
    const paths = args.url.split('/');
    paths.forEach((path, i) => {
      if (path.startsWith(':')) {
        const key = path.slice(1);
        paths[i] = args.params[key];
        delete args.params[key];
      }
    });

    return args.config.apiHost + args.config.urlPrefix + paths.join('/');
  }

  get<T = any>(url: string, params?: HttpParams | any, options?: GenericApiOption): Observable<T> {
    const request = (config: AppConfig) => {
      params = GenericApiService.pretreatmentDataRequest(params, options);
      const defaultRequestOptions = {
        ...this.defaultOptions,
        ...{ requestOptions: { params: params, headers: this.createHeaders() } },
      };
      options = GenericApiService.normalizeOption(defaultRequestOptions, options);
      const fullUrl = this.createAPIURL({ url, params, config });

      return this.httpClient.get(fullUrl, options.requestOptions).pipe(
        timeout(this.requestTimeout),
        map(response => (options?.pretreatmentResponse ? response['data'] : response))
      );
    };

    return this.configService.withConfig(config => request(config)).pipe(take(1));
  }

  post<T = any>(url: string, params?: any, options?: GenericApiOption): Observable<T> {
    const request = (config: AppConfig) => {
      params = GenericApiService.pretreatmentDataRequest(params, options);
      options = GenericApiService.normalizeOption(this.defaultOptions, options);
      const fullUrl = this.createAPIURL({ url, params, config });

      return this.httpClient.post<T>(fullUrl, params, options?.requestOptions).pipe(
        timeout(this.requestTimeout),
        map(response => (options?.pretreatmentResponse ? response['data'] : response))
      );
    };

    return this.configService.withConfig(config => request(config)).pipe(take(1));
  }

  put<T = any>(url: string, params?: any, options?: GenericApiOption): Observable<T> {
    const request = (config: AppConfig) => {
      params = GenericApiService.pretreatmentDataRequest(params, options);
      options = GenericApiService.normalizeOption(this.defaultOptions, options);
      const fullUrl = this.createAPIURL({ url, params, config });

      return this.httpClient.put(fullUrl, params, options.requestOptions).pipe(
        timeout(this.requestTimeout),
        map(response => (options?.pretreatmentResponse ? response['data'] : response))
      );
    };

    return this.configService.withConfig(config => request(config)).pipe(take(1));
  }

  patch<T = any>(url: string, params?: any, options?: GenericApiOption): Observable<T> {
    const request = (config: AppConfig) => {
      params = GenericApiService.pretreatmentDataRequest(params, options);
      options = GenericApiService.normalizeOption(this.defaultOptions, options);
      const fullUrl = this.createAPIURL({ url, params, config });

      return this.httpClient.patch(fullUrl, params, options.requestOptions).pipe(
        timeout(this.requestTimeout),
        map(response => (options?.pretreatmentResponse ? response['data'] : response))
      );
    };

    return this.configService.withConfig(config => request(config)).pipe(take(1));
  }

  delete<T = any>(url: string, params?: any, options?: GenericApiOption): Observable<T> {
    const request = (config: AppConfig) => {
      params = GenericApiService.pretreatmentDataRequest(params, options);
      const defaultRequestOptions = {
        ...this.defaultOptions,
        ...{ requestOptions: { body: params, headers: this.createHeaders() } },
      };
      options = GenericApiService.normalizeOption(defaultRequestOptions, options);
      const fullUrl = this.createAPIURL({ url, params, config });

      return this.httpClient.request('delete', fullUrl, options.requestOptions).pipe(
        timeout(this.requestTimeout),
        map(response => (options?.pretreatmentResponse ? response['data'] : response))
      );
    };

    return this.configService.withConfig(config => request(config)).pipe(take(1));
  }
}
